import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import nfcHeroMockup from "@/assets/nfc-hero-mockup.png";
import nfcTapPhone from "@/assets/nfc-tap-phone.png";

const STRIPE_CHECKOUT_URL = "https://buy.stripe.com/7sYbJ1dpzg9T5bqeNF2oE00";

const includedItems = [
  "2 custom-branded NFC business cards",
  "2 encoded NFC stickers",
  "Pre-linked to your QCard digital profile",
  "Tap-to-share with any modern smartphone",
];

const NFCPurchaseSection = () => {
  return (
    <section className="relative py-16 md:py-24 bg-gradient-to-br from-slate-950 via-purple-950/40 to-slate-950 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-72 h-72 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">One-Time Purchase</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Upgrade to{" "}
              <span className="bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent">
                Custom NFC Cards
              </span>
            </h2>
            <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto">
              Take your networking physical. Tap to share your QCard instantly — no apps, no QR codes needed.
            </p>
          </div>

          {/* Product Card */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 md:p-10 shadow-2xl">
            {/* Images */}
            <div className="relative space-y-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 to-purple-500/30 blur-2xl rounded-3xl" />
                <img
                  src={nfcTapPhone}
                  alt="NFC keychain tag tapping an iPhone to share a QCard digital business card"
                  className="relative w-full h-auto rounded-2xl object-contain"
                  loading="lazy"
                />
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/20 to-primary/20 blur-2xl rounded-3xl" />
                <img
                  src={nfcHeroMockup}
                  alt="Custom NFC business cards and stickers bundle"
                  className="relative w-full h-auto rounded-2xl object-contain"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Details */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                NFC Card & Sticker Bundle
              </h3>
              <p className="text-white/70 mb-6 leading-relaxed">
                Includes 2 custom-branded and encoded NFC cards and 2 encoded NFC stickers, linked to your digital QCard business card for easy sharing and networking.
              </p>

              {/* What's included */}
              <ul className="space-y-3 mb-8">
                {includedItems.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-primary" strokeWidth={3} />
                    </div>
                    <span className="text-white/80 text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button
                size="lg"
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground text-base md:text-lg px-8 py-6 font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all"
                asChild
              >
                <a
                  href={STRIPE_CHECKOUT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Order Your NFC Bundle
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <p className="text-white/50 text-xs md:text-sm mt-3">
                Secure checkout powered by Stripe • Ships worldwide
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NFCPurchaseSection;
